import { useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import { buildMuiTheme } from "./theme/muiTheme";
import { useColorScheme } from "./hooks/useColorScheme";
import { AuthGate } from "./features/auth/AuthGate";
import { ProfileScreen } from "./features/profile/ProfileScreen";
import { SessionsScreen } from "./features/profile/SessionsScreen";
import { ToastHost } from "./features/shared/ToastHost";
import { DialogHost } from "./features/shared/DialogHost";
import { AlbumPickerDialog } from "./features/shared/AlbumPickerDialog";
import { AlbumsListScreen } from "./features/albums/AlbumsListScreen";
import { CommentThread } from "./features/comments/CommentThread";
import { NotificationsDialog } from "./features/notifications/NotificationsDialog";
import { MusicPanel } from "./features/music/MusicPanel";
import { GalleryScreen } from "./features/gallery/GalleryScreen";
import { UploadWidget } from "./features/upload/UploadWidget";
import { AddScreen } from "./features/upload/AddScreen";
import { ChatScreen } from "./features/chat/ChatScreen";
import { GiftsScreen } from "./features/gifts/GiftsScreen";
import { SendGiftScreen } from "./features/gifts/SendGiftScreen";
import { RewardsScreen } from "./features/gifts/RewardsScreen";
import { PrivateDropLockedPage } from "./features/gifts/PrivateDropLockedPage";
import { ReelDialog } from "./features/reel/ReelDialog";
import { TogetherRoot } from "./features/together/TogetherRoot";
import { WatchTogetherScreen } from "./features/together/WatchTogetherScreen";
import { PartyRoot } from "./features/party/PartyRoot";
import { GameRoot } from "./features/game/GameRoot";
import { CoupleWheelPage } from "./features/wheel/CoupleWheelPage";
import { PresenceIndicator } from "./features/presence/PresenceIndicator";
import { AppNav } from "./features/nav/AppNav";
import { CoupleHeader } from "./features/home/CoupleHeader";
import { ActivityCarousel } from "./features/home/ActivityCarousel";
import { RecentMemories } from "./features/home/RecentMemories";
import { HomeRouteScreen } from "./routes/HomeRouteScreen";
import { GalleryRouteScreen } from "./routes/GalleryRouteScreen";
import { AlbumsRouteScreen } from "./routes/AlbumsRouteScreen";
import { MusicRouteScreen } from "./routes/MusicRouteScreen";
import { ChatListRouteScreen } from "./routes/ChatListRouteScreen";
import { ChatThreadRouteScreen } from "./routes/ChatThreadRouteScreen";
import { GiftsRouteScreen } from "./routes/GiftsRouteScreen";
import { SendGiftRouteScreen } from "./routes/SendGiftRouteScreen";
import { RewardsRouteScreen } from "./routes/RewardsRouteScreen";
import { PrivateDropRouteScreen } from "./routes/PrivateDropRouteScreen";
import { WatchTogetherRouteScreen } from "./routes/WatchTogetherRouteScreen";
import { AddRouteScreen } from "./routes/AddRouteScreen";
import { ProfileRouteScreen } from "./routes/ProfileRouteScreen";
import { SessionsRouteScreen } from "./routes/SessionsRouteScreen";
import { CoupleWheelRouteScreen } from "./features/wheel/CoupleWheelRouteScreen";

// Tapping a native push notification (sw.js's notificationclick) focuses
// an already-open tab rather than opening a new one, then postMessages the
// intended in-app destination here - a plain window.location change would
// work too, but throws away all client-side state for a full reload just
// to move to a route this SPA can already reach instantly. useNavigate()
// needs a component actually rendered inside <BrowserRouter>, which
// AppRoot's own body isn't (it's what RENDERS the router), hence this
// small child instead of wiring the listener directly into AppRoot.
function PushNavigationListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "push-navigate" && typeof event.data.url === "string") {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
  return null;
}

// Permanently-mounted root housing every migrated feature. Gains one child
// per migration phase (Profile: Phase 2; toast/dialogs/album-picker shared
// utilities: Phase 3; Albums/Comments/Notifications: Phase 4; Music: Phase 5;
// Gallery/Lightbox/Sort/Select: Phase 6; Upload: Phase 7; Session/Auth +
// Autolock: Phase 8; Chat: Phase 9; Nav/routing consolidation + dedicated
// Music page: Phase 10; Gifts: added post-Phase-10, same route-screen
// pattern).
//
// ScopedCssBaseline, not the global CssBaseline, so MUI's reset doesn't
// leak into not-yet-migrated Bootstrap/vanilla markup elsewhere on the
// page. Gotcha: MUI Dialog/Popover/Snackbar portal to document.body by
// default, *outside* this scoped subtree - every dialog-like component
// goes through AppDialog, which pins container={"#react-root"} to stay
// inside the scope.
//
// Routing design (Phase 10): the <Routes> below render only thin,
// content-less "route-effect" components (src/react/routes/*) that toggle
// a static container's d-none class and set the appState fields navigating
// there implies - real URLs, browser back/forward, and deep linking, on
// top of the exact same container-portal architecture every other screen
// already uses. The screens that actually render content (GalleryScreen,
// AlbumsListScreen, MusicPanel, ChatScreen) stay unconditionally mounted
// here exactly as before, reading appState reactively - <Routes> was
// deliberately kept out of their mount/unmount lifecycle, since
// GalleryScreen's header controls are static vanilla buttons wired once
// via getVanillaEl and must never be torn down by a route change.
export function AppRoot() {
  const mode = useColorScheme();
  const theme = useMemo(() => buildMuiTheme(mode), [mode]);

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <ScopedCssBaseline>
          <PushNavigationListener />
          <AuthGate />
          <ProfileScreen />
          <SessionsScreen />
          <ToastHost />
          <DialogHost />
          <AlbumPickerDialog />
          <AlbumsListScreen />
          <CommentThread />
          <NotificationsDialog />
          <MusicPanel />
          <GalleryScreen />
          <UploadWidget />
          <AddScreen />
          <ChatScreen />
          <GiftsScreen />
          <SendGiftScreen />
          <RewardsScreen />
          <PrivateDropLockedPage />
          <ReelDialog />
          <TogetherRoot />
          <WatchTogetherScreen />
          <PartyRoot />
          <GameRoot />
          <CoupleWheelPage />
          <PresenceIndicator />
          <AppNav />
          <CoupleHeader />
          <ActivityCarousel />
          <RecentMemories />
          <Routes>
            <Route path="/" element={<HomeRouteScreen />} />
            <Route path="/gallery" element={<GalleryRouteScreen />} />
            <Route path="/albums" element={<AlbumsRouteScreen />} />
            <Route path="/albums/:albumId" element={<GalleryRouteScreen />} />
            <Route path="/music" element={<MusicRouteScreen />} />
            <Route path="/chat" element={<ChatListRouteScreen />} />
            <Route path="/chat/:conversationId" element={<ChatThreadRouteScreen />} />
            <Route path="/gifts" element={<GiftsRouteScreen />} />
            <Route path="/gifts/send" element={<SendGiftRouteScreen />} />
            <Route path="/gifts/drop/:id" element={<PrivateDropRouteScreen />} />
            <Route path="/rewards" element={<RewardsRouteScreen />} />
            <Route path="/watch-together" element={<WatchTogetherRouteScreen />} />
            <Route path="/add" element={<AddRouteScreen />} />
            <Route path="/profile" element={<ProfileRouteScreen />} />
            <Route path="/profile/sessions" element={<SessionsRouteScreen />} />
            <Route path="/couple-wheel" element={<CoupleWheelRouteScreen />} />
          </Routes>
        </ScopedCssBaseline>
      </ThemeProvider>
    </BrowserRouter>
  );
}
